import { useEffect, useRef, useState } from "react";
import type { TradeDoc } from "./tradeProposalTypes";
import { acceptTrade, cancelTrade, declineTrade, listMyTrades } from "./tradeProposalApi";
import { formatCurrency } from "./tradeHelpers";
import { useFocusTrap } from "./useFocusTrap";
import { toast } from "@/components/ui/use-toast";

type Props = {
  open: boolean;
  uid: string;
  onClose(): void;
  onLoadIntoBuilder(doc: TradeDoc): void;
};

export default function TradeInboxModal({ open, uid, onClose, onLoadIntoBuilder }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<(TradeDoc & { id: string })[]>([]);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  useFocusTrap({ active: open, containerRef: panelRef, initialFocusRef: closeBtnRef, onEscape: onClose });

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!open || !uid) return;
      setBusy(true);
      setErr(null);
      try {
        const list = await listMyTrades(uid);
        if (mounted) setRows(list);
      } catch (e: any) {
        if (mounted) setErr(e?.message || "Failed to load trade proposals.");
      } finally {
        if (mounted) setBusy(false);
      }
    }
    void load();
    return () => {
      mounted = false;
    };
  }, [open, uid]);

  async function act(action: "accept" | "decline" | "cancel", id: string) {
    setBusy(true);
    setErr(null);
    try {
      if (action === "accept") {
        await acceptTrade(uid, id);
        toast({ variant: "success", title: "Proposal accepted" });
      }
      if (action === "decline") {
        await declineTrade(uid, id);
        toast({ variant: "info", title: "Proposal declined" });
      }
      if (action === "cancel") {
        await cancelTrade(uid, id);
        toast({ variant: "info", title: "Proposal cancelled" });
      }
      const list = await listMyTrades(uid);
      setRows(list);
    } catch (e: any) {
      const msg = e?.message || "Action failed.";
      setErr(msg);
      toast({ variant: "destructive", title: "Trade action failed", description: msg });
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" aria-hidden={!open}>
      <div className="absolute inset-0 bg-black/70" onClick={() => !busy && onClose()} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trade-inbox-title"
        className="relative z-10 w-full max-w-3xl rounded-md border border-gray-800 bg-gray-950 p-4 shadow-2xl"
        tabIndex={-1}
      >
        <div className="mb-3 flex items-center justify-between">
          <div id="trade-inbox-title" className="text-lg font-semibold text-gray-100">Trade Inbox</div>
          <button
            ref={closeBtnRef}
            className="rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5"
            onClick={onClose}
            aria-label="Close trade inbox"
          >
            Close
          </button>
        </div>

        {err && (
          <div className="mb-3 rounded border border-red-800 bg-red-900/20 p-2 text-sm text-red-200">{err}</div>
        )}

        <div className="max-h-[70vh] overflow-auto rounded border border-gray-800">
          {busy ? (
            <div className="p-6 text-center text-sm text-gray-300">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">No proposals.</div>
          ) : (
            <ul className="divide-y divide-gray-800">
              {rows.map((t) => {
                const iAmProposer = t.proposerUid === uid;
                const mine = iAmProposer ? t.proposerTotals : t.recipientTotals;
                return (
                  <li key={t.id} className="flex items-center gap-3 px-2 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-gray-100">
                        {iAmProposer ? "You proposed" : "Proposed to you"} • Status:{" "}
                        <span className="uppercase">{t.status}</span>
                      </div>
                      {t.message && <div className="text-xs text-gray-400">“{t.message}”</div>}
                      <div className="text-xs text-gray-400">
                        You send {formatCurrency(mine.sent)} • You receive {formatCurrency(mine.received)} • Diff:{" "}
                        {formatCurrency(mine.diff)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5"
                        onClick={() => onLoadIntoBuilder(t)}
                        title="Load into builder"
                      >
                        Load
                      </button>
                      {iAmProposer ? (
                        t.status === "open" ? (
                          <button
                            className="rounded border border-red-800 bg-red-900/30 px-2 py-1 text-red-200 hover:bg-red-900/50"
                            onClick={() => void act("cancel", t.id!)}
                          >
                            Cancel
                          </button>
                        ) : null
                      ) : t.status === "open" ? (
                        <>
                          <button
                            className="rounded border border-emerald-800 bg-emerald-900/30 px-2 py-1 text-emerald-200 hover:bg-emerald-900/50"
                            onClick={() => void act("accept", t.id!)}
                          >
                            Accept
                          </button>
                          <button
                            className="rounded border border-amber-800 bg-amber-900/30 px-2 py-1 text-amber-200 hover:bg-amber-900/50"
                            onClick={() => void act("decline", t.id!)}
                          >
                            Decline
                          </button>
                        </>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
